import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "./Modal";
import { Checkbox, formatDate } from "./FieldComponents";
import { ChevronRight, Folder, File as FileSvg, Search } from "lucide-react";
import {
    isDocumentCaseLogicalPathAllowed,
    isDocumentCaseStorageItemAllowed
} from '../utils/documentCaseBoundary';

const formatSize = (size) => {
    if (size === null || size === undefined) return "-";
    if (!size || size === 0) return "0";
    return size < 1024
        ? `${size} B`
        : size < 1024 * 1024
        ? `${(size / 1024).toFixed(1)} KB`
        : `${(size / 1024 / 1024).toFixed(1)} MB`;
};

const buildChildPath = (basePath, childName) => {
    if (!basePath || basePath === "root") {
        return childName;
    }
    return `${basePath}/${childName}`;
};

export const MoveFolderPicker = ({
    isOpen,
    onClose,
    onSelect,
    fetchFolderContents,
    caseName,
    currentFolderPath = "",
    initialPath = "{{Name}}",
    initialSelection = "",
    maxPageSize = 500,
    selectMode = "folder",
    title = "Choose destination folder",
    instructions = "Navigate to a folder and select it with the checkbox. Click the current breadcrumb to select the current folder.",
    confirmLabel = "Choose Destination"
}) => {
    const [currentPath, setCurrentPath] = useState(initialPath);
    const [isPathInitialized, setIsPathInitialized] = useState(false);
    const [items, setItems] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(Math.min(200, maxPageSize));
    const [selectedPath, setSelectedPath] = useState(initialSelection);
    const [selectedItem, setSelectedItem] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const input = useRef(null);
    const startPathRef = useRef(currentFolderPath || initialPath || "{{Name}}");
    const requestSeqRef = useRef(0);

    useEffect(() => {
        if (!isOpen) return;
        setIsPathInitialized(false);
        const requestedStartPath = currentFolderPath || initialPath || "{{Name}}";
        const startPath = requestedStartPath === 'root' ? '{{Name}}' : requestedStartPath;
        if (!isDocumentCaseLogicalPathAllowed(startPath)) {
            startPathRef.current = '{{Name}}';
            setCurrentPath('{{Name}}');
            setSelectedPath('{{Name}}');
            setItems([]);
            return;
        }
        startPathRef.current = startPath;
        setCurrentPath(startPath);
        setPage(1);
        setSelectedPath(isDocumentCaseLogicalPathAllowed(initialSelection) ? initialSelection : startPath);
        setSelectedItem(null);
        setSearchQuery("");
    }, [isOpen, currentFolderPath, initialPath, initialSelection]);

    useEffect(() => {
        if (!isOpen) return;
        if (currentPath === startPathRef.current) {
            setIsPathInitialized(true);
        }
    }, [isOpen, currentPath]);

    useEffect(() => {
        let isCancelled = false;

        const load = async () => {
            if (!isOpen || !fetchFolderContents || !isPathInitialized) return;
            if (!currentPath || !isDocumentCaseLogicalPathAllowed(currentPath)) {
                setItems([]);
                setPagination(null);
                return;
            }

            const requestId = ++requestSeqRef.current;
            setIsLoading(true);
            try {
                const result = await fetchFolderContents(currentPath, page, pageSize);
                if (isCancelled || requestId !== requestSeqRef.current) return;
                const foldersRaw = result?.folders;
                const filesRaw = result?.files;
                const folders = Array.isArray(foldersRaw)
                    ? foldersRaw
                    : (foldersRaw && typeof foldersRaw === "object" ? Object.values(foldersRaw) : []);
                const files = Array.isArray(filesRaw)
                    ? filesRaw
                    : (filesRaw && typeof filesRaw === "object" ? Object.values(filesRaw) : []);

                const folderItems = folders
                    .filter(folder => folder?.name
                        && folder.name !== "(This folder)"
                        && isDocumentCaseStorageItemAllowed({ ...folder, type: 'folder' }, currentPath))
                    .map(folder => ({
                        type: "folder",
                        name: folder.name,
                        last_modified: folder.last_modified,
                        size: folder.size
                    }));

                const fileItems = files
                    .filter(file => isDocumentCaseStorageItemAllowed({ ...file, type: 'file' }, currentPath))
                    .map(file => ({
                    type: "file",
                    id: file.id || file.file_id || null,
                    name: file.name,
                    key: file.key || file.file_path || file.path || null,
                    file_path: file.file_path || file.key || file.path || null,
                    file_url: file.file_url || file.url || null,
                    last_modified: file.last_modified,
                    size: file.size
                }));

                setItems([...folderItems, ...fileItems]);
                const visibleItemCount = folderItems.length + fileItems.length;
                const rejectedRows = visibleItemCount < folders.length + files.length;
                setPagination(rejectedRows ? {
                    current_page: page,
                    total_pages: 1,
                    total_items: visibleItemCount,
                    page_size: pageSize,
                    has_next: false,
                    has_prev: false
                } : (result?.pagination || null));
            } catch (error) {
                if (isCancelled || requestId !== requestSeqRef.current) return;
                console.error("Failed to load folder picker contents:", error);
                setItems([]);
                setPagination(null);
            } finally {
                if (isCancelled || requestId !== requestSeqRef.current) return;
                setIsLoading(false);
            }
        };

        load();

        return () => {
            isCancelled = true;
        };
    }, [isOpen, currentPath, page, pageSize, fetchFolderContents, isPathInitialized]);

    useEffect(() => {
        if (!isOpen) return;
        if (!selectedPath) {
            setSelectedPath(currentPath || "{{Name}}");
        }
    }, [isOpen, currentPath, selectedPath]);

    const breadcrumbs = useMemo(() => {
        const crumbs = [{ label: caseName, path: "{{Name}}" }];
        if (!currentPath || currentPath === "{{Name}}") return crumbs;
        const trimmed = currentPath.replace(/^\{\{Name\}\}\/?/, "");
        const parts = trimmed.split("/").filter(Boolean);
        let running = "{{Name}}";
        parts.forEach(part => {
            running = `${running}/${part}`;
            crumbs.push({ label: part, path: running });
        });
        return crumbs;
    }, [currentPath, caseName]);

    const handleOpenFolder = (folderName) => {
        const nextPath = buildChildPath(currentPath || "{{Name}}", folderName);
        if (!isDocumentCaseLogicalPathAllowed(nextPath)) return;
        setCurrentPath(nextPath);
        setPage(1);
        setSelectedPath(nextPath);
    };

    const handleSelectFolder = (folderName) => {
        const path = buildChildPath(currentPath || "{{Name}}", folderName);
        if (!isDocumentCaseLogicalPathAllowed(path)) return;
        setSelectedPath(prev => (prev === path ? (currentPath || "{{Name}}") : path));
        setSelectedItem(null);
    };

    const handleSelectFile = (file) => {
        if (selectMode !== "file") return;
        const path = file.file_path || file.key || buildChildPath(currentPath || "{{Name}}", file.name);
        setSelectedPath(path);
        setSelectedItem(file);
    };

    const handleConfirm = () => {
        if (!selectedPath && !currentPath) return;
        if (selectMode === "file") {
            const selectedItemPath = selectedItem?.file_path || selectedItem?.key || '';
            if (!selectedItem
                || selectedPath !== selectedItemPath
                || !isDocumentCaseStorageItemAllowed(
                    { ...selectedItem, type: 'file' },
                    currentPath
                )) return;
            onSelect?.(selectedPath, selectedItem);
            return;
        }
        let resolvedPath = selectedPath || currentPath || "{{Name}}";
        if (resolvedPath === "{{Name}}" && currentPath && currentPath !== "{{Name}}") {
            resolvedPath = currentPath;
        }
        if (!isDocumentCaseLogicalPathAllowed(resolvedPath)) return;
        onSelect?.(resolvedPath);
    };

    const handleBack = () => {
        if (!currentPath || currentPath === "{{Name}}") return;
        const parts = currentPath.replace(/^\{\{Name\}\}\/?/, "").split("/").filter(Boolean);
        const nextPath = parts.length > 1
            ? `{{Name}}/${parts.slice(0, -1).join("/")}`
            : "{{Name}}";
        setCurrentPath(nextPath);
        setPage(1);
        setSelectedPath(nextPath);
    };

    const filteredItems = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return items;
        return items.filter(item => (item?.name || "").toLowerCase().includes(q));
    }, [items, searchQuery]);

    const totalItems = pagination?.total_items ?? items.length;

    return (
        <Modal
            title={title}
            instructions={instructions}
            onClose={onClose}
            zIndex={2000}
            wider
            header={
                <div className='modal-header-actions'>
                    <button onClick={onClose} className="action alt">Cancel</button>
                </div>
            }
            subheader={
                <>
                    <div className="move-picker-breadcrumbs subtext" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '12px', textDecoration: 'underline' }}>
                        {breadcrumbs.map((crumb, index) => (
                            <React.Fragment key={crumb.path}>
                                {index > 0 && <ChevronRight size={16} />}
                                <span
                                    style={{
                                        cursor: 'pointer',
                                        color: index === breadcrumbs.length - 1 ? 'inherit' : 'var(--primary-color)',
                                        fontWeight: selectedPath === crumb.path ? 600 : 400
                                    }}
                                    onClick={() => {
                                        if (index !== breadcrumbs.length - 1) {
                                            setCurrentPath(crumb.path);
                                            setPage(1);
                                        }
                                        setSelectedPath(crumb.path);
                                    }}
                                >
                                    {crumb.label}
                                </span>
                            </React.Fragment>
                        ))}
                    </div>

                    <div style={{ fontSize: '20px' }}>
                        Items <span style={{ color: 'var(--subtext-color)' }}>({totalItems})</span>
                    </div>

                    <div className='folder-search' style={{ marginBottom: '12px' }} onClick={() => input.current?.focus()}>
                        <Search size={18} />
                        <input
                            type="text"
                            ref={input}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search this folder..."
                        />
                    </div>
                </>
            }
            footer={
                <div className='modal-footer-actions'>
                    <button onClick={handleBack} className="action alt" disabled={!currentPath || currentPath === "{{Name}}"}>
                        Back
                    </button>
                    <button onClick={handleConfirm} className="action" disabled={!selectedPath}>
                        {confirmLabel}
                    </button>
                </div>
            }
        >
            <div className="modal-content-wrapper">
                <div className="move-picker-table">
                    <table className="employee-data-table" style={{ paddingBottom: '10px' }}>
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left', paddingLeft: '68px', width: '65%' }}>Name</th>
                                <th style={{ width: '15%' }}>Last Modified</th>
                                <th style={{ width: '10%' }}>Size</th>
                                <th style={{ width: '10%' }}>Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr className='subtext'>
                                    <td colSpan={4} className="subtext" style={{ textAlign: 'center', padding: '16px' }}>
                                        Loading...
                                    </td>
                                </tr>
                            ) : (
                                <>
                                    {filteredItems.length === 0 ? (
                                        <tr className='subtext'>
                                            <td colSpan={4} className="subtext" style={{ textAlign: 'center', padding: '16px' }}>
                                                This folder is empty.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredItems.map((item, index) => (
                                            <tr
                                                key={`${item.type}-${item.name}-${index}`}
                                                className={`small-text item-cont ${item.type === 'folder' ? 'folder-cursor' : ''}`}
                                                style={{ backgroundColor: 'var(--background-color)', cursor: item.type === 'folder' ? 'pointer' : 'default' }}
                                                onClick={() => {
                                                    if (item.type === 'folder') {
                                                        handleOpenFolder(item.name);
                                                    }
                                                }}
                                            >
                                                <td className="subtext move-table-name" style={{ width: '65%'}}>
                                                    {item.type === 'folder' ? (
                                                        <Checkbox
                                                            value={selectedPath === buildChildPath(currentPath || "{{Name}}", item.name)}
                                                            onChange={(e) => {
                                                                e?.stopPropagation?.();
                                                                handleSelectFolder(item.name);
                                                            }}
                                                            mini
                                                        />
                                                    ) : selectMode === 'file' ? (
                                                        <Checkbox
                                                            value={selectedItem?.name === item.name && selectedPath === (item.file_path || item.key || buildChildPath(currentPath || "{{Name}}", item.name))}
                                                            onChange={(e) => {
                                                                e?.stopPropagation?.();
                                                                handleSelectFile(item);
                                                            }}
                                                            mini
                                                        />
                                                    ) : (
                                                        <span style={{ display: 'inline-block', width: '18px' }} />
                                                    )}
                                                    {item.type === 'folder' ? <Folder size={16} /> : <FileSvg size={16} />}
                                                    <span style={{ marginLeft: '6px' }}>{item.name}</span>
                                                </td>
                                                <td className="subtext" style={{ width: '15%' }}>
                                                    {String(item.last_modified || '').length > 1 ? formatDate(item.last_modified)[0] : "-"}
                                                </td>
                                                <td className="subtext" style={{ width: '10%' }}>
                                                    {item.type === 'folder' ? `${item.size || 0} items` : formatSize(item.size)}
                                                </td>
                                                <td className="subtext" style={{ width: '10%' }}>
                                                    {item.type === 'folder' ? 'Folder' : 'File'}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>

                {pagination && (pagination.total_pages > 1 || pagination.has_next || pagination.has_prev) && (
                    <div className="doc-pagination-controls" style={{ marginTop: '12px' }}>
                        <div className="pagination-info">
                            <span className="subtext">
                                Showing {(pagination.current_page - 1) * pagination.page_size + 1} - {Math.min(pagination.current_page * pagination.page_size, pagination.total_items)} of {pagination.total_items} items
                            </span>
                            <div className="page-size-selector" style={{ marginLeft: '16px' }}>
                                <span className="subtext" style={{ marginRight: '8px' }}>Items per page:</span>
                                <select
                                    value={pageSize}
                                    onChange={(e) => {
                                        const next = Math.min(maxPageSize, parseInt(e.target.value, 10));
                                        setPageSize(next);
                                        setPage(1);
                                    }}
                                    className="default-select"
                                    style={{ minWidth: '70px' }}
                                >
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={200}>200</option>
                                    <option value={500}>500</option>
                                </select>
                            </div>
                        </div>
                        <div className="pagination-buttons">
                            <button
                                className={`action ${!pagination.has_prev ? 'disabled' : ''}`}
                                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                                disabled={!pagination.has_prev}
                            >
                                Previous
                            </button>
                            <span className="pagination-current">
                                Page {pagination.current_page} of {pagination.total_pages}
                            </span>
                            <button
                                className={`action ${!pagination.has_next ? 'disabled' : ''}`}
                                onClick={() => setPage(prev => prev + 1)}
                                disabled={!pagination.has_next}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default MoveFolderPicker;
